import { ChevronRight } from "lucide-react";
import { StageBadge } from "./StageBadge";
import { otherTracksForDisplay } from "./lifecycle-sheet-helpers";
import type { TrackState } from "../types";

interface OtherTracksStripProps {
  tracks: Record<string, TrackState>;
  onTrackClick: (trackName: string) => void;
}

export function OtherTracksStrip({
  tracks,
  onTrackClick,
}: OtherTracksStripProps) {
  const entries = otherTracksForDisplay(tracks);

  return (
    <div className="border-border mt-6 border-t pt-5">
      <h3 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide uppercase">
        Other tracks
      </h3>
      <div className="space-y-2">
        {entries.map(({ name, state }) => {
          if (!state) {
            // The merchant has not entered this track — show it disabled with
            // an "empty" status rather than hiding it, so the strip always
            // lists the full set of tracks.
            return (
              <div
                key={name}
                aria-disabled="true"
                className="border-border bg-muted/50 flex w-full items-center justify-between rounded-md border px-3 py-2.5 opacity-60"
              >
                <span className="text-subtle-foreground text-sm font-medium uppercase">
                  {name}
                </span>
                <StageBadge stage="empty" />
              </div>
            );
          }

          const payoutStatus =
            name === "referral"
              ? ((state.metadata?.payout as { status?: string } | undefined)
                  ?.status ?? null)
              : null;
          const hasError = payoutStatus === "failed";

          return (
            <button
              key={name}
              type="button"
              onClick={() => onTrackClick(name)}
              className="border-border bg-muted hover:border-muted-foreground/40 hover:bg-accent flex w-full items-center justify-between rounded-md border px-3 py-2.5 transition-colors"
            >
              <span className="text-foreground text-sm font-medium uppercase">
                {name}
              </span>
              <div className="flex items-center gap-2">
                <StageBadge stage={state.stage} hasError={hasError} />
                <ChevronRight className="text-subtle-foreground h-3.5 w-3.5" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
